/**
 * Background Objects Component
 * 
 * Renders decorative 2D objects behind the UI content
 * Objects are subtle, elegant, and non-distracting
 */

import React from 'react';
import { getBackgroundObjects, shouldShowObjects } from '@/config/objectsTheme';
import { useVFXConfig } from '@/features/vfx/hooks/useVFXConfig';
import { useStyleHasArt } from '@/hooks/useStyleHasArt';

export const BackgroundObjects: React.FC = () => {
  const { backgroundConfig, isLoading } = useVFXConfig();
  // Decorative objects are background art: styles without art (Daylight, Luxe) draw none.
  const styleHasArt = useStyleHasArt();

  // Get objects theme from API config
  // Note: background.theme is for app background gradient (1-10)
  //       background.objectsTheme is for decorative objects (0-10)
  // Only render if API explicitly provides objectsTheme
  const themeId = backgroundConfig?.theme;

  // Don't render if:
  // - Still loading config
  // - No theme from API
  // - Theme is 0 (disabled)
  if (!styleHasArt || isLoading || themeId === undefined || !shouldShowObjects(themeId)) {
    return null;
  }

  const objects = getBackgroundObjects(themeId);

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 10 }}
      aria-hidden="true"
    >
      {objects.map((obj) => (
        <div
          key={obj.id}
          className="absolute select-none"
          style={{
            ...obj.position,
            fontSize: obj.size,
            opacity: obj.opacity,
            transform: obj.rotation ? `rotate(${obj.rotation}deg)` : undefined,
            zIndex: obj.zIndex,
            filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))', // Subtle shadow for depth
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
        >
          {obj.emoji}
        </div>
      ))}
    </div>
  );
};

export default BackgroundObjects;

